import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ENDPOINTS } from '../constants/endpoints';

// ---- P1-B customer portal: its own HTTP client, never shares the staff bearer or the staff 401 handling ----
export const PORTAL_TOKEN_KEY = 'portalToken';

export const getPortalToken = (): string | null => {
  try { return sessionStorage.getItem(PORTAL_TOKEN_KEY); } catch { return null; }
};
export const setPortalToken = (token: string): void => {
  try { sessionStorage.setItem(PORTAL_TOKEN_KEY, token); } catch { /* storage blocked: the session lasts the page only */ }
};
export const clearPortalToken = (): void => {
  try { sessionStorage.removeItem(PORTAL_TOKEN_KEY); } catch { /* nothing to clear */ }
};

/** The institution code is the second path segment of /portal/<code>/... */
export const portalCodeFromPath = (path: string = window.location.pathname): string | null => {
  const m = /^\/portal\/([^/]+)/.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
};

let onUnauthorized: ((code: string | null) => void) | null = null;
/** PortalRoot registers a router-aware redirect; the fallback is a hard navigation. */
export const setPortalUnauthorizedHandler = (fn: ((code: string | null) => void) | null): void => { onUnauthorized = fn; };

let instance: AxiosInstance | null = null;

/**
 * axios.create copies axios.defaults, so a staff bearer already set in this browser would ride along.
 * The request interceptor therefore ALWAYS overwrites or deletes Authorization. The instance is created lazily
 * so importing this module never touches axios at load time.
 */
export const getPortalHttp = (): AxiosInstance => {
  if (instance) return instance;
  const http = axios.create({ baseURL: ENDPOINTS.PORTAL.BASE });
  http.interceptors.request.use(config => {
    const token = getPortalToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    else config.headers.delete('Authorization');
    return config;
  });
  http.interceptors.response.use(
    (r: AxiosResponse) => r,
    error => {
      // Only a 401 on a request that carried a portal session means "the session ended"; a wrong password
      // on the login page is also a 401 and must stay on the page.
      if (error?.response?.status === 401 && getPortalToken()) {
        clearPortalToken();
        const code = portalCodeFromPath();
        if (onUnauthorized) onUnauthorized(code);
        else if (code) window.location.assign(`/portal/${encodeURIComponent(code)}/login`);
      }
      return Promise.reject(error);
    },
  );
  instance = http;
  return http;
};

export const portalHttp = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => getPortalHttp().get<T>(url, config),
  post: <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig) => getPortalHttp().post<T>(url, data, config),
};

/** The error text the portal API returns as {"error": msg}. */
export const portalErrorMessage = (e: any, fallback: string): string => {
  const d = e?.response?.data;
  if (d && typeof d.error === 'string') return d.error;
  if (e?.response?.status === 404) return 'Not found';
  return fallback;
};
// ---- end P1-B ----
